import React, { useState } from "react";

// Importing bootstrap components
import { Col, Card, Form, Button, Spinner } from "react-bootstrap";

// Importing formik & yup
import { Formik } from "formik";
import * as Yup from "yup";

// Importing toast utilities
import { showSuccessToast } from "utils/toast";

import { useDispatch, useSelector } from "react-redux";

// Importing team slices
import {
  addTeam as addTeamToStore,
  updateTeam as updateTeamInStore,
  deleteTeam as deleteTeamFromStore,
} from "state/slices/team/teamSlice";

import {
  useCreateTeamMutation,
  useUpdateTeamMutation,
  useDeleteTeamMutation,
} from "state/slices/team/teamApiSlice";

// Importing user slices
import { updateUserInfo } from "state/slices/auth/authSlice";
import { useGetUsersQuery } from "state/slices/user/userApiSlice";

// Import Utilities
import { isUserOwnTheData } from "utils/authentication";

const initialValues = {
  id: null,
  name: "",
  isDefault: "",
  teamLeadId: "",
};

const GeneralAccessManagement = React.memo(
  ({
    isAllowedToRead,
    isAllowedToCreate,
    isAllowedToUpdate,
    isAllowedToDelete,
    TeamViewManagement,
    isAllowedToReadEmployees,
  }) => {
    const [selectedRow, setSelectedRow] = useState(null);
    const [formikValues, setFormikValues] = useState(initialValues);

    const dispatch = useDispatch();

    const { userInfo: loggedUserInfo } = useSelector((state) => state.auth);

    const [createTeam] = useCreateTeamMutation();
    const [updateTeam] = useUpdateTeamMutation();
    const [deleteTeam] = useDeleteTeamMutation();

    const { data: users = [], refetch: userRefetch } = useGetUsersQuery();

    const handleCheckboxChange = (event, row) => {
      if (event.target.checked) {
        setSelectedRow(row);
      } else {
        setSelectedRow(null);
        setFormikValues(initialValues);
      }
    };

    const handleEdit = (row) => {
      setFormikValues(row);
    };

    const addTeam = async (teamData) => {
      try {
        const data = await createTeam(teamData).unwrap();

        dispatch(addTeamToStore(data));

        showSuccessToast("Team added successfully");
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const updateTeamManage = async (team, teamData) => {
      try {
        const data = await updateTeam({
          id: team,
          data: teamData,
        }).unwrap();

        const updatedTeamData = {
          ...data.team,
        };

        // Update Team in client side
        dispatch(updateTeamInStore(updatedTeamData));

        // Update team of current user
        if (isUserOwnTheData(loggedUserInfo, "team", data?.team.id)) {
          dispatch(
            updateUserInfo({
              field: "team",
              data: data.team,
            })
          );
        }

        showSuccessToast("Team updated successfully");

        userRefetch();
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const deleteTeamManage = async () => {
      try {
        console.log(selectedRow);
        await deleteTeam(selectedRow.id).unwrap();
        dispatch(deleteTeamFromStore(selectedRow.id));
        showSuccessToast("Team deleted successfully");
        userRefetch();

        if (selectedRow.id) {
          setFormikValues(initialValues);
          setSelectedRow(null);
        }
      } catch (err) {
        // Error toast is automatically shown by baseQuery
      }
    };

    const handleSubmit = (values, { resetForm }) => {
      let { isDefault, id, ...restValues } = values;

      // Manage teamLeadId & idDefault according to server communication
      if (!restValues.teamLeadId) {
        restValues.teamLeadId = null;
      }

      isDefault = (isDefault === "true" || isDefault) === true ? true : false;

      if (id) {
        const updatedValues = {
          ...restValues,
          id,
          isDefault,
        };

        updateTeamManage(id, updatedValues);

        // Rest Form Data
        setFormikValues(initialValues);
      } else {
        addTeam({
          ...restValues,
          isDefault,
        });
      }

      if (selectedRow?.id) {
        setSelectedRow(null);
      }

      resetForm();
    };

    const validationSchema = Yup.object().shape({
      name: Yup.string().required("Team name is required"),
      isDefault: Yup.string().required("Default Team is required"),
      teamLeadId: Yup.string(),
    });

    return (
      <section className="section dashboard">
        {(isAllowedToCreate || isAllowedToUpdate) && (
          <Card>
            <Card.Body>
              <Formik
                enableReinitialize={true}
                initialValues={formikValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
              >
                {({ values, handleChange, handleSubmit, touched, errors, isSubmitting }) => (
                  <Form noValidate onSubmit={handleSubmit} className="row mt-3">
                    <Col md={4} className="mb-3 mb-md-0">
                      <Form.Group controlId="name">
                        <Form.Control
                          type="text"
                          name="name"
                          placeholder="Enter team name"
                          value={values.name}
                          onChange={handleChange}
                          isInvalid={touched.name && !!errors.name}
                          disabled={isSubmitting}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={3} className="mb-3 mb-md-0">
                      <Form.Control
                        as="select"
                        name="teamLeadId"
                        value={values.teamLeadId}
                        onChange={handleChange}
                        isInvalid={touched.teamLeadId && !!errors.teamLeadId}
                        disabled={isSubmitting}
                      >
                        <option value="">Select Team Leader</option>
                        {users.map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name}
                          </option>
                        ))}
                      </Form.Control>
                      <Form.Control.Feedback type="invalid">
                        {errors.teamLeadId}
                      </Form.Control.Feedback>
                    </Col>
                    <Col md={3} className="mb-3 mb-md-0">
                      <Form.Group controlId="isDefault">
                        <Form.Control
                          as="select"
                          name="isDefault"
                          value={values.isDefault}
                          onChange={handleChange}
                          isInvalid={touched.isDefault && !!errors.isDefault}
                          disabled={isSubmitting}
                        >
                          <option value="">Set as default team</option>
                          <option value={false}>No</option>
                          <option value={true}>Yes</option>
                        </Form.Control>
                        <Form.Control.Feedback type="invalid">
                          {errors.isDefault}
                        </Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col md={2}>
                      <Button variant="primary" className="w-100" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                          <>
                            <Spinner
                              animation="border"
                              size="sm"
                              role="status"
                              className="me-2"
                              style={{ width: "18px", height: "18px" }}
                            />
                            {formikValues.id ? "Updating..." : "Adding..."}
                          </>
                        ) : (
                          formikValues.id ? "Save" : "Add"
                        )}
                      </Button>
                    </Col>
                  </Form>
                )}
              </Formik>
            </Card.Body>
          </Card>
        )}

        {isAllowedToRead && (
          <TeamViewManagement
            isAllowedToUpdate={isAllowedToUpdate}
            isAllowedToDelete={isAllowedToDelete}
            isAllowedToReadEmployees={isAllowedToReadEmployees}
            handleCheckboxChange={handleCheckboxChange}
            handleEdit={handleEdit}
            deleteTeamManage={deleteTeamManage}
            selectedRow={selectedRow}
          />
        )}
      </section>
    );
  }
);

export default GeneralAccessManagement;
